import React, { useState } from "react";
import {
  Car,
  Gauge,
  Settings,
  ShieldAlert,
  Ticket,
  Calendar,
  Clock,
  Mail,
  Phone,
  Map,
  MapPin,
} from "lucide-react";
import { camelToNormal } from "../../common/caseconversion";
import formatDateAndTime from "../../common/formatDateAndTime";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import { capsconversion } from "../../common/capsconversion";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import toast from "react-hot-toast";

const BookingDetailsCard = ({
  getAllHubsListData,
  bookingDetailsData,
  systemUserData,
  vehicleDetails
}) => {
  const fleetBookingStatus = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.fleetBookingStatus;
  let bookingTypes = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.bookingTypes;
  const vehicleType = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.vehicleType;
  const [copied, setCopied] = useState(false);

  const handleCopy = (e, text) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text || "N/A");
    toast.success("Copied", {
      position: "top-center",
      style: {
        background: "white", // override background
        color: "green",
        fontWeight: "medium",
      },
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // hide after 2 sec
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4">
        {/* Vehicle Details */}
        <div className="bg-blue-50 border rounded-2xl shadow hover:shadow-md transition p-5">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">
            Vehicle Details
          </h3>

          <div className="flex flex-col items-center space-y-3">
            <div className="w-44 h-28 object-cover flex items-center justify-center border rounded-xl bg-white overflow-hidden">
              {bookingDetailsData?.vehicleDetails?.imageUrl ? (
                <img
                  src={bookingDetailsData.vehicleDetails.imageUrl}
                  alt="Vehicle"
                  className="object-cover w-full h-full"
                />
              ) : (
                <span className="text-gray-400 text-xs">No Image</span>
              )}
            </div>

            <div className="w-full space-y-2 text-sm">
              <p className="flex items-center justify-between gap-2">
                <span className="font-semibold flex items-center gap-2">
                  <Car size={18} className="text-blue-700" />
                  Registration:
                </span>
                <span>
                  {bookingDetailsData?.vehicleRegistrationNo || "N/A"}
                </span>
              </p>
              <p className="flex items-center justify-between gap-2">
                <span className="font-semibold flex items-center gap-2">
                  <Gauge size={18} className="text-blue-700" />
                  Vehicle Name:
                </span>
                <span>{bookingDetailsData?.vehicleName || "N/A"}</span>
              </p>
              <p className="flex items-center justify-between gap-2">
                <span className="font-semibold flex items-center gap-2">
                  <Gauge size={18} className="text-blue-700" />
                  Vehicle Type:
                </span>
                <span>{vehicleType[bookingDetailsData?.vehicleType] || "N/A"}</span>
              </p>
              {String(bookingTypes?.inverted?.electricCars) === String(bookingDetailsData?.bookingType) && (
                <>                  
                <p className="flex items-center justify-between gap-2">
                  <span className="font-semibold flex items-center gap-2">
                    <Gauge size={18} className="text-blue-700" />
                    Driving Range:
                  </span>
                  <span>{vehicleDetails?.drivingRange || "N/A"}</span>
                </p>
                <p className="flex items-center justify-between gap-2">
                  <span className="font-semibold flex items-center gap-2">
                    <Gauge size={18} className="text-blue-700" />
                    Charging Time:
                  </span>
                  <span>{vehicleDetails?.chargingTime || "N/A"}</span>
                </p>
                <p className="flex items-center justify-between gap-2">
                  <span className="font-semibold flex items-center gap-2">
                    <Gauge size={18} className="text-blue-700" />
                    Charging Type:
                  </span>
                  <span>{vehicleDetails?.chargingType || "N/A"}</span>
                </p>
                </>
              )}
              <p className="flex items-center justify-between gap-2">
                <span className="font-semibold flex items-center gap-2">
                  <Gauge size={18} className="text-blue-700" />
                  Model:
                </span>
                <span>
                  {bookingDetailsData?.vehicleDetails?.vehicleModelText ||
                    "N/A"}
                </span>
              </p>
              <p className="flex items-center justify-between gap-2">
                <span className="font-semibold flex items-center gap-2">
                  <Settings size={18} className="text-blue-700" />
                  Fuel Type:
                </span>
                <span>
                  {bookingDetailsData?.vehicleDetails?.fuelType || "N/A"}
                </span>
              </p>
              <p className="flex items-center justify-between gap-2">
                <span className="font-semibold flex items-center gap-2">
                  <Settings size={18} className="text-blue-700" />
                  Transmission:
                </span>
                <span>
                  {bookingDetailsData?.vehicleDetails?.transmissionType ||
                    "N/A"}
                </span>
              </p>
              <p className="flex items-center justify-between gap-2 text-red-600">
                <span className="font-semibold flex items-center gap-2">
                  <ShieldAlert size={18} />
                  Latest QC Status:
                </span>
                <span>{bookingDetailsData?.qcStatus || "N/A"}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Booking Details */}
        <div className="bg-green-50 border rounded-2xl shadow hover:shadow-md transition p-5">
          <h3 className="text-lg font-semibold text-green-900 mb-4">
            Booking Details
          </h3>
          <div className="space-y-2 text-sm">
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Ticket size={18} className="text-green-700" />
                Booking Status:
              </span>
              <span className="bg-red-300 text-red font-bold px-4 rounded-lg">
                {fleetBookingStatus[bookingDetailsData?.bookingStatus] || "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Ticket size={18} className="text-green-700" />
                Booking ID:
              </span>
              <span>{bookingDetailsData?.bookingCode || "N/A"}</span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Ticket size={18} className="text-green-700" />
                Inquiry ID:
              </span>
              <span>{bookingDetailsData?.inquiryCode || "N/A"}</span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Calendar size={18} className="text-green-700" />
                Inquiry Created at:
              </span>
              <span>
                {formatDateAndTime(bookingDetailsData?.createdAt) || "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Calendar size={18} className="text-green-700" />
                Booking Generated at:
              </span>
              <span>
                {formatDateAndTime(bookingDetailsData?.bookingGeneratedOn) ||
                  "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Ticket size={18} className="text-green-700" />
                City:
              </span>
              <span>{bookingDetailsData?.cityText || "N/A"}</span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Clock size={18} className="text-green-700" />
                Start Date:
              </span>
              <span>
                {formatDateAndTime(bookingDetailsData?.bookFrom) || "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Clock size={18} className="text-green-700" />
                End Date:
              </span>
              <span>
                {formatDateAndTime(bookingDetailsData?.bookTo) || "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Map size={18} className="text-green-700" />
                Total Booking Hrs:
              </span>
              <span>{bookingDetailsData?.bookingHrs + " hrs" || "N/A"}</span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Map size={18} className="text-green-700" />
                Package Plan:
              </span>
              <span>
                {bookingDetailsData?.packageDetails?.name
                  ? `${bookingDetailsData.packageDetails.name} Kms`
                  : bookingDetailsData?.packageDetails?.packageName
                    ? bookingDetailsData?.packageDetails?.packageName +
                    "( " +
                    bookingDetailsData?.packageDetails?.minDayCount +
                    "day Rental )"
                    : "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Map size={18} className="text-green-700" />
                Overall Free Kms:
              </span>
              <span>
                {bookingDetailsData?.packageDetails?.totalFreeKm
                  ? `${bookingDetailsData.packageDetails.totalFreeKm} Kms`
                  : "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Map size={18} className="text-green-700" />
                Extra Km Cost:
              </span>
              <span>
                {bookingDetailsData?.packageDetails?.extraKmCost
                  ? `${bookingDetailsData.packageDetails.extraKmCost} Rs`
                  : "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Map size={18} className="text-green-700" />
                Booking Type:
              </span>
              <span>
                {(bookingDetailsData?.bookingType &&
                  bookingTypes[bookingDetailsData?.bookingType]) ||
                  bookingTypes[bookingTypes?.inverted?.dailyRental]}
              </span>
            </p>
          </div>
        </div>

        {/* Customer Details */}
        <div className="bg-yellow-50 border rounded-2xl shadow hover:shadow-md transition p-5">
          <h3 className="text-lg font-semibold text-yellow-900 mb-4">
            Customer Details
          </h3>
          <div className="space-y-2 text-sm">
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold">Customer Name:</span>
              <span>{bookingDetailsData?.customerDetails?.name || "N/A"}</span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Mail size={18} className="text-yellow-700" />
                Email:
              </span>
              <a
                href={`mailto:${bookingDetailsData?.customerDetails?.email}`}
                id="mailto"
                className="underline"
              >
                {bookingDetailsData?.customerDetails?.email || "N/A"}
              </a>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                <Phone size={18} className="text-yellow-700" />
                Phone:
              </span>
              <span>
                {bookingDetailsData?.customerDetails?.mobile || "N/A"}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Additional Booking Info */}
        <div className="bg-purple-50 border rounded-2xl shadow hover:shadow-md transition p-5">
          <h3 className="text-lg font-semibold text-purple-900 mb-4">
            Additional Booking Info
          </h3>
          <div className="space-y-2 text-sm">
            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Hub Address :
              </span>
              <span
                title={
                  getAllHubsListData &&
                  getAllHubsListData.find(
                    (item) => bookingDetailsData?.hubId === item?.id,
                  )?.hubAddress
                }
                className="bg-red-300 text-red font-bold px-4 rounded-lg text-right"
              >
                {(getAllHubsListData &&
                  getAllHubsListData.find(
                    (item) => bookingDetailsData?.hubId === item?.id,
                  )?.hubAddress) ||
                  "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Source:
              </span>
              <span className="bg-red-300 text-red font-bold px-4 rounded-lg text-right">
                {capsconversion(bookingDetailsData?.source) || "N/A"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Delivery Address:
              </span>
              <span
                title={bookingDetailsData?.deliveryAddress}
                className="bg-red-300 text-red font-bold px-4 flex justify-between items-center rounded-lg w-64 truncate text-right"
              >
                {capsconversion(bookingDetailsData?.deliveryAddress) || "N/A"}
                <button
                  onClick={(e) => {
                    handleCopy(e, bookingDetailsData?.deliveryAddress);
                  }}
                  className="ml-2 p-1 rounded hover:bg-red-400 transition-colors"
                >
                  <ContentCopyIcon
                    sx={{
                      fontSize: 18,
                      cursor: "pointer",
                      color: "#1d1c1cff",
                    }}
                  />
                </button>
                {copied && (
                  <span className="absolute -top-6 right-0 bg-black text-white text-xs px-2 py-1 rounded">
                    Copied!
                  </span>
                )}
              </span>
            </p>

            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Pickup Address:
              </span>
              <span
                title={bookingDetailsData?.pickupAddress}
                className="bg-red-300 text-red font-bold px-4 flex justify-between items-center rounded-lg w-64 truncate text-right"
              >
                {capsconversion(bookingDetailsData?.pickupAddress) || "N/A"}
                <button
                  onClick={(e) => {
                    handleCopy(e, bookingDetailsData?.pickupAddress);
                  }}
                  className="ml-2 p-1 rounded hover:bg-red-400 transition-colors"
                >
                  <ContentCopyIcon
                    sx={{
                      fontSize: 18,
                      cursor: "pointer",
                      color: "#161515ff",
                    }}
                  />
                </button>
                {copied && (
                  <span className="absolute -top-6 right-0 bg-black text-white text-xs px-2 py-1 rounded">
                    Copied!
                  </span>
                )}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Delivery Type :
              </span>
              <span className="text-right font-semibold">
                {camelToNormal(bookingDetailsData?.deliveryType) || "-"}
              </span>
            </p>

            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Pickup Type :
              </span>
              <span className="text-right font-semibold">
                {camelToNormal(bookingDetailsData?.pickupType) || "-"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Destination :
              </span>
              <span
                title={bookingDetailsData?.deliveryAddress}
                className="bg-red-300 text-red font-bold px-4 rounded-lg w-28 truncate text-right"
              >
                {capsconversion(bookingDetailsData?.destination) || "-"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Created By :
              </span>
              <span className="text-right font-semibold">
                {systemUserData?.find(
                  (item) => bookingDetailsData?.createdBy === item?.id,
                )?.name || "-"}
              </span>
            </p>
            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Assigned To :
              </span>
              <span className="text-right font-semibold">
                {systemUserData?.find(
                  (item) => bookingDetailsData?.assignedAgent === item?.id,
                )?.name || "-"}
              </span>
            </p>
            {/* <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                <MapPin size={18} className="text-purple-700" />
                Converted By :
              </span>
              <span className="text-right font-semibold">
                {systemUserData?.find(
                  (item) => bookingDetailsData?.convertedBy === item?.id
                )?.name || "-"}
              </span>
            </p> */}
          </div>
        </div>

        <div className="bg-red-50 border rounded-2xl shadow hover:shadow-md transition p-5">
          <h3 className="text-lg font-semibold text-red-900 mb-4">
            Booking Costing Details
          </h3>
          <div className="space-y-2 text-sm">
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold">Cost to Customer:</span>
              <span>{"₹ " + bookingDetailsData?.costToCustomer || 0}</span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                Total Amount Paid:
              </span>
              <span>
                ₹{" "}
                {(bookingDetailsData?.totalAmountPaid &&
                  bookingDetailsData?.totalAmountPaid?.toFixed(2)) ||
                  0}
              </span>
            </p>
            {/* <p className="flex items-center justify-between gap-2">
              <span className="font-semibold flex items-center gap-2">
                Pending Amount:
              </span>
              <span>
                {"₹ " +
                  Number((bookingDetailsData?.pendingAmount || 0).toFixed(2))}
              </span>
            </p> */}
            <p className="flex items-center justify-between gap-2 break-words">
              <span className="font-semibold flex items-center gap-2">
                Comment:
              </span>
              <span
                title={bookingDetailsData?.comment}
                className="bg-red-300 text-red font-bold px-4 rounded-lg text-right w-66 truncate"
              >
                {capsconversion(bookingDetailsData?.comment) || "-"}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            className="shadow-lg rounded-lg"
          >
            <Typography component="span">
              Base Billing Details (Package:{" "}
              {(bookingDetailsData?.packageDetails?.name
                ? bookingDetailsData.packageDetails.name + " Km"
                : bookingDetailsData?.packageDetails?.packageName) || "N/A"}
              )
            </Typography>
          </AccordionSummary>

          <div className="p-1 h-60 overflow-auto">
            <AccordionDetails>
              <div className="px-2">
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white border-collapse text-md">
                      <thead>
                        <tr className="bg-blue-100 text-gray-700">
                          <th className="p-2 text-left">Description</th>
                          <th className="p-2 text-left">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Weekday Rent Row */}
                        <tr className="border-b border-gray-200">
                          <td className="p-2 flex items-center space-x-1">
                            <span>Weekday Rent</span>
                            <small className="text-gray-500">
                              ({bookingDetailsData?.weekdayHrs || 0} Hrs)
                            </small>
                            <small className="text-gray-500">
                              (
                              {bookingDetailsData?.packageDetails
                                ?.weekdayTariff || 0}{" "}
                              / Hr)
                            </small>
                          </td>
                          <td className="p-2 text-blue-600 font-medium">
                            {"₹ " + bookingDetailsData?.weekdayRent || 0}
                          </td>
                        </tr>

                        {/* Weekend Rent Row */}
                        <tr className="border-b border-gray-200">
                          <td className="p-2 flex items-center space-x-1">
                            <span>Weekend Rent</span>
                            <small className="text-gray-500">
                              ({bookingDetailsData?.weekendHrs || 0} Hrs)
                            </small>
                            <small className="text-gray-500">
                              (
                              {bookingDetailsData?.packageDetails
                                ?.weekendTariff || 0}{" "}
                              / Hr)
                            </small>
                          </td>
                          <td className="p-2 text-blue-600 font-medium">
                            {"₹ " + bookingDetailsData?.weekendRent || 0}
                          </td>
                        </tr>

                        {/* Discount Amount Row */}
                        <tr className="border-b border-gray-200">
                          <td className="p-2 flex items-center space-x-1">
                            <span>Discount Amount</span>
                            <small className="text-gray-500">
                              ({bookingDetailsData?.discountPercentage || 0} %)
                            </small>
                          </td>
                          <td className="p-2 text-green-600 font-medium">
                            {"₹ " + bookingDetailsData?.discountAmount || 0}
                          </td>
                        </tr>

                        {/* Tax Amount Row */}
                        <tr className="border-b border-gray-200">
                          <td className="p-2">Tax</td>
                          <td className="p-2 text-red-600 font-medium">
                            {"₹ " + bookingDetailsData?.taxAmount || 0}
                          </td>
                        </tr>

                        {/* Security Deposit Row */}
                        <tr className="border-b border-gray-200">
                          <td className="p-2">Security Deposit</td>
                          <td className="p-2 text-blue-600 font-medium">
                            {"₹ " + bookingDetailsData?.securityDeposit || 0}
                          </td>
                        </tr>

                        {/* Pickup Charges Row */}
                        <tr className="border-b border-gray-200">
                          <td className="p-2">Pickup Charges</td>
                          <td className="p-2 text-blue-600 font-medium">
                            {"₹ " + bookingDetailsData?.pickupCharges || 0}
                          </td>
                        </tr>

                        {/* Delivery Charges Row */}
                        <tr className="border-b border-gray-200">
                          <td className="p-2">Delivery Charges</td>
                          <td className="p-2 text-blue-600 font-medium">
                            {"₹ " + bookingDetailsData?.deliveryCharges || 0}
                          </td>
                        </tr>
                        {/* Final Rent */}
                        <tr>
                          <td className="p-2">Rental</td>
                          <td className="p-2 text-red-600 font-medium">
                            {"₹ " +
                              (
                                bookingDetailsData?.weekdayRent +
                                bookingDetailsData?.weekendRent +
                                bookingDetailsData?.taxAmount +
                                bookingDetailsData?.securityDeposit +
                                bookingDetailsData?.pickupCharges +
                                bookingDetailsData?.deliveryCharges -
                                bookingDetailsData?.discountAmount
                              ).toFixed(2) || "Not Specified"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </div>

          {/* </>
                ) : (
                  <h1 className="text-center text-xl font-medium my-6">
                    No Alarm Counts
                  </h1>
                )} */}
        </Accordion>
      </div>
    </div>
  );
};

// Example usage
export default BookingDetailsCard;
